import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Share2, ImageDown, Loader2, CheckCircle2 } from "lucide-react";
import { resolvedImageUrl } from "@/lib/api";
import { WhatsAppIcon } from "@/components/public/BrandIcons";

async function fetchResolvedFile(ref) {
  const res = await fetch(resolvedImageUrl(ref));
  if (!res.ok) throw new Error("Resolved card image not available");
  const blob = await res.blob();
  return new File([blob], `hoodi-ward-29-${ref}.png`, { type: blob.type || "image/png" });
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

/** Shareable "Issue Resolved" proof card for a resolved ward issue. */
export default function ResolvedCardShare({ referenceId, title }) {
  const trackUrl = `${window.location.origin}/track-issue?ref=${referenceId}`;
  const [sharing, setSharing] = useState(false);

  // Pre-fetch so navigator.share() can run inside the click gesture on mobile
  const { data: file } = useQuery({
    queryKey: ["resolved-card-file", referenceId],
    queryFn: () => fetchResolvedFile(referenceId),
    staleTime: Infinity,
    retry: 0,
  });

  const text = `Resolved in Hoodi Ward 29 — ${title}\nReference ${referenceId}\n${trackUrl}`;

  const share = async () => {
    setSharing(true);
    try {
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Issue Resolved — Hoodi Ward 29", text });
      } else if (navigator.share) {
        await navigator.share({ title: "Issue Resolved — Hoodi Ward 29", text, url: trackUrl });
      } else {
        throw new Error("share-unsupported");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      const copied = await copyText(text);
      toast[copied ? "success" : "info"](
        copied ? "Sharing isn't supported here — details copied. Paste anywhere to share." : `Copy this link to share: ${trackUrl}`
      );
    } finally {
      setSharing(false);
    }
  };

  const download = async () => {
    try {
      const f = file || (await fetchResolvedFile(referenceId));
      const a = document.createElement("a");
      a.href = URL.createObjectURL(f);
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Resolved card downloaded");
    } catch {
      toast.error("Could not download the resolved card");
    }
  };

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");

  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-navy/60 p-5 md:p-6" data-testid="resolved-card-share">
      <p className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-civic font-bold mb-4">
        <CheckCircle2 className="h-3.5 w-3.5" /> Resolved — share the proof
      </p>
      <div className="grid sm:grid-cols-2 gap-5 items-start">
        <div className="rounded-xl overflow-hidden border border-white/15 shadow-xl">
          <img src={resolvedImageUrl(referenceId)} alt={`Resolved card for ${referenceId}`} className="w-full"
            data-testid="resolved-card-preview" />
        </div>
        <div>
          <p className="text-sm text-slate-300 leading-relaxed">
            This auto-generated before/after card shows the issue has been closed. Share it with your neighbours so
            everyone in Hoodi Ward 29 can see the follow-up.
          </p>
          <div className="mt-5 flex flex-col gap-2.5">
            <button onClick={share} disabled={sharing} data-testid="resolved-card-share-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-civic text-white font-bold text-sm hover:brightness-110 transition-all disabled:opacity-60">
              {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share Resolved Card
            </button>
            <button onClick={shareWhatsApp} data-testid="resolved-card-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-bold text-sm hover:bg-[#1ebe5b] transition-colors">
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp
            </button>
            <button onClick={download} data-testid="resolved-card-download"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white font-bold text-sm hover:border-gold hover:text-gold transition-colors">
              <ImageDown className="h-4 w-4" /> Download Image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Compact share control for the public "Recently Resolved" wall (no image pre-fetch). */
export function ResolvedShareLink({ referenceId, title, testId }) {
  const trackUrl = `${window.location.origin}/track-issue?ref=${referenceId}`;
  const text = `Resolved in Hoodi Ward 29 — ${title}\nReference ${referenceId}\n${trackUrl}`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Issue Resolved — Hoodi Ward 29", text, url: trackUrl });
        return;
      }
      throw new Error("share-unsupported");
    } catch (err) {
      if (err?.name === "AbortError") return;
      const copied = await copyText(text);
      toast[copied ? "success" : "info"](copied ? "Details copied — paste anywhere to share." : trackUrl);
    }
  };

  return (
    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
      <button onClick={share} data-testid={testId}
        className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider font-bold text-navy hover:text-gold transition-colors">
        <Share2 className="h-3.5 w-3.5" /> Share
      </button>
      <a href={resolvedImageUrl(referenceId)} target="_blank" rel="noreferrer"
        data-testid={`${testId}-card`}
        className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider font-bold text-navy hover:text-gold transition-colors">
        <ImageDown className="h-3.5 w-3.5" /> Resolved Card
      </a>
    </div>
  );
}
